import { useI18N } from '@yandex-int/i18n/lib/react';
import React, { useEffect, useState } from 'react';

import * as keyset from 'i18n/SmartHomeForm';

import { ApiError, getCloudFunctions, validateCloudFunction } from '../../api';
import { CloudError, CloudFunction } from '../../types/cloud';
import EnhancedSelect from '../EnhancedSelect/EnhancedSelect';
import { Loading } from '../Loading/Loading';

import { emptyFunctionsDescription, functionsCaption } from './texts';

interface FunctionsContainerProps {
    onFunctionIdChange: (value: string) => void;
    value: string;
    disabled?: boolean;
    renderFunctionsListError: (code: CloudError) => React.ReactElement;
    renderValidationError: (code: CloudError) => React.ReactElement;
}

const FunctionsContainer: React.FC<FunctionsContainerProps> = ({
    onFunctionIdChange,
    disabled,
    value,
    renderFunctionsListError,
    renderValidationError,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [functions, setFunctions] = useState<CloudFunction[]>([]);
    const [functionsListErrorCode, setFunctionsListErrorCode] = useState<CloudError | undefined>(undefined);
    const [validationErrorCode, setValidationErrorCode] = useState<CloudError | undefined>(undefined);

    const t = useI18N(keyset);

    const loadFunctions = async () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);

        try {
            setFunctions(await getCloudFunctions());
        } catch (e) {
            if (e instanceof ApiError && e.payload && e.payload.errorCode) {
                setFunctionsListErrorCode(e.payload.errorCode as CloudError);
            }
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const validateFunctionOnChange = async () => {
        setValidationErrorCode(undefined);

        if (!value) {
            return;
        }

        try {
            await validateCloudFunction(value);
        } catch (e) {
            if (e instanceof ApiError && e.payload && e.payload.errorCode) {
                setValidationErrorCode(e.payload.errorCode as CloudError);

                return;
            }

            setValidationErrorCode(CloudError.UNKNOWN);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void loadFunctions(), []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void validateFunctionOnChange(), [value]);

    if (isLoading) {
        return <Loading centered size="m" />;
    }

    if (functionsListErrorCode) {
        return renderFunctionsListError(functionsListErrorCode);
    }

    if (functions.length === 0) {
        return emptyFunctionsDescription;
    }

    return (
        <>
            <EnhancedSelect
                namespace="cloud_functions"
                nameSelector={(x) => `${x.cloudName}/${x.folderName}/${x.functionName}`}
                items={functions}
                idSelector={(x) => x.functionId}
                onChange={onFunctionIdChange}
                disabled={disabled}
                value={value}
                defaultValue={{
                    label: t('not-chosen'),
                    value: '',
                }}
            />
            {validationErrorCode ? renderValidationError(validationErrorCode) : null}
            {functionsCaption}
        </>
    );
};

export default FunctionsContainer;
